import { CustomerClassifications } from '@mews-commander/data-access';
import {
    customerClassificationIconNameHandler,
    mapCustomerClassifications,
    useCommanderSelector,
} from '@mews-commander/utils';
import { FeatureFlag } from '@mews-commander/enums';
import {
    getLoyaltyMembership,
    getMostValuableLoyaltyMembership,
} from '@mews-commander/legacy-entities/src/ducks/loyaltyMemberships';
import { getCustomerClassifications } from '@mews-commander/legacy-entities/src/ducks/customers';
import { LocalizedEnum, useFeatureFlag } from '@mews/framework';
import { BareIconButton, Stack, Tooltip } from '@optimus-web/core';
import { CompactedCustomerClassification } from './CompactedCustomerClassification';
import { ClassificationsLabel } from './ClassificationsLabel';

const IconButtonWithTooltip = Tooltip(BareIconButton);

const mapAllClassifications = (classification: number, onClick: () => void, featureFlagEnabled: boolean) => {
    const classifications = mapCustomerClassifications(classification);

    const filteredClassifications = featureFlagEnabled
        ? classifications.filter((c) => c.name !== CustomerClassifications.Returning)
        : classifications;

    return filteredClassifications.map((c, index) => {
        const iconHandle = customerClassificationIconNameHandler(c.value);

        if (!iconHandle) return null;

        return featureFlagEnabled && index === 0 ? (
            <Stack spacing="50" alignment="center" inline>
                <IconButtonWithTooltip
                    icon={iconHandle.iconName}
                    size="small"
                    onClick={onClick}
                    tooltip={<LocalizedEnum prefix="CustomerClassifications" value={c.name} />}
                />
            </Stack>
        ) : (
            <Stack spacing="50" alignment="center" inline>
                <BareIconButton icon={iconHandle.iconName} size="small" onClick={onClick} />
                <span>
                    <LocalizedEnum prefix="CustomerClassifications" value={c.name} />
                </span>
            </Stack>
        );
    });
};

interface CustomerClassificationsLabelProps {
    customerId: string;
    handleClassificationClick: () => void;
}

export const CustomerClassificationsLabel = ({
    customerId,
    handleClassificationClick,
}: CustomerClassificationsLabelProps) => {
    const smartDetailReturningIconEnabled = useFeatureFlag(FeatureFlag.smartDetailReturningIconEnabled);

    const loyaltyMembershipId = useCommanderSelector((state) =>
        getMostValuableLoyaltyMembership(state, { accountId: customerId })
    );
    const loyaltyMembership = useCommanderSelector((state) =>
        loyaltyMembershipId ? getLoyaltyMembership(state, { id: loyaltyMembershipId }) : null
    );
    const customerClassification = useCommanderSelector((state) =>
        getCustomerClassifications(state, { id: customerId })
    );

    const classifications = customerClassification
        ? mapAllClassifications(customerClassification, handleClassificationClick, smartDetailReturningIconEnabled)
        : [];

    return smartDetailReturningIconEnabled ? (
        <ClassificationsLabel
            loyaltyMembership={loyaltyMembership}
            classifications={classifications}
            customerId={customerId}
        />
    ) : (
        <CompactedCustomerClassification loyaltyMembership={loyaltyMembership} classifications={classifications} />
    );
};
